import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LogOut, ChevronDown } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { NAV_ITEMS, ADMIN_ITEM, isNavItemActive } from '@/components/app/navItems';
import UserAvatar from '@/components/app/UserAvatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/*
 * Collapse is done in CSS, not JS state: md is the icon rail, lg is the full
 * sidebar. A resize listener would be one more thing to get out of sync, and
 * would flash the wrong width on first paint.
 */

function NavRow({ item, active }) {
  const { to, label, icon: Icon } = item;
  return (
    <Link
      to={to}
      title={label}
      aria-current={active ? 'page' : undefined}
      data-testid={`nav-${to.slice(1)}`}
      className={`
        flex items-center h-11 rounded-[var(--dd-radius)]
        transition-colors
        justify-center w-11 mx-auto
        lg:justify-start lg:w-auto lg:mx-0 lg:px-3 lg:gap-3
        ${active ? 'bg-dd-lime-ghost text-dd-lime dd-glow-soft' : 'text-dd-muted hover:text-dd-text hover:bg-dd-s2'}
      `}
    >
      <Icon className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={active ? 2.2 : 1.8} />
      <span className="hidden lg:block text-sm font-medium truncate">{label}</span>
    </Link>
  );
}

export default function Sidebar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const items = user?.is_admin ? [...NAV_ITEMS, ADMIN_ITEM] : NAV_ITEMS;

  return (
    <aside
      data-testid="app-sidebar"
      className="
        fixed inset-y-0 left-0 z-40 hidden md:flex flex-col
        bg-dd-s1 border-r border-dd-line
        w-[72px] lg:w-[240px]
      "
    >
      <div className="flex items-center h-[72px] flex-shrink-0 justify-center lg:justify-start lg:px-5">
        <Link to="/dashboard" className="flex items-center gap-2.5 min-w-0" aria-label="DriverDash">
          <img src="/dd-icon.png" alt="" className="w-7 h-7 brand-accent-logo flex-shrink-0" />
          <span className="hidden lg:block text-[17px] font-bold tracking-tight text-dd-text truncate">
            DriverDash
          </span>
        </Link>
      </div>

      <nav className="flex-1 flex flex-col gap-1 px-3">
        {items.map((item) => (
          <NavRow key={item.to} item={item} active={isNavItemActive(location.pathname, item.to)} />
        ))}
      </nav>

      <div className="p-3 border-t border-dd-line">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              data-testid="sidebar-user"
              className="
                w-full flex items-center rounded-[var(--dd-radius)]
                transition-colors hover:bg-dd-s2
                justify-center py-2 lg:justify-start lg:gap-3 lg:p-2
              "
            >
              <UserAvatar size={36} showStatus />
              <span className="hidden lg:block min-w-0 flex-1 text-left">
                <span className="block text-[13px] font-semibold text-dd-text truncate">{user?.name}</span>
                <span className="block text-[11px] text-dd-dim truncate">{user?.email}</span>
              </span>
              <ChevronDown className="hidden lg:block w-4 h-4 text-dd-dim flex-shrink-0" />
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            align="start"
            side="top"
            className="w-56 border-dd-line bg-dd-s2 text-dd-text rounded-[var(--dd-radius)]"
          >
            <DropdownMenuItem
              onSelect={() => navigate('/settings')}
              className="cursor-pointer text-dd-muted focus:bg-dd-s3 focus:text-dd-text"
            >
              Instellingen
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-dd-line" />
            <DropdownMenuItem
              onSelect={logout}
              data-testid="logout-button"
              className="cursor-pointer text-dd-muted focus:bg-dd-negative/10 focus:text-dd-negative"
            >
              <LogOut className="w-4 h-4" />
              Uitloggen
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
  );
}
